// Keyboard movement between matrix rows (the deferred F5 "keyboard cell nav").
//
// THE LOOP THIS SERVES: reviewing a long matrix is read requirement → set
// status → next row → repeat, several hundred times. Without this, getting from
// one row's status to the next means either the mouse or a run of Tab presses
// through remarks, row actions and the next row's checkbox.
//
// WHY Alt+ARROW AND NOT A PLAIN ARROW: the fields are real inputs, selects and
// textareas. A plain arrow has to keep moving the caret and changing the select
// value, or typing breaks. Alt is unused elsewhere in the app — Ctrl/Meta is
// undo/redo (which itself bails inside form fields), and dnd-kit's keyboard
// drag takes plain arrows only while a drag is active, which starts from the
// grip button rather than a field.
//
// WHY DOM TRAVERSAL RATHER THAN ROW INDICES: the table renders only the rows
// that survive the status filter and the search box, so walking siblings gives
// "the next row the user can actually see" for free. An index into the model
// would happily jump to a hidden row.

/** The column classes a cell can be identified by. */
const COLUMN_CLASS = /^c-[a-z]+$/;

/**
 * Controls worth landing on. Read-only provenance cells hold none, so they are
 * skipped.
 *
 * Kept as separate tags and combined per column below, because a CSS selector
 * list does NOT distribute over its prefix: `td.c-rem input, select, textarea`
 * is three selectors, the last two of which match anything in the row. That
 * returned the status dropdown no matter which column was asked for.
 */
const FOCUSABLE_TAGS = ["input", "select", "textarea"];

/** The `c-*` class naming this cell's column, or null. */
export function columnClassOf(cell: Element | null): string | null {
  if (!cell) return null;
  for (const cls of Array.from(cell.classList)) {
    if (COLUMN_CLASS.test(cls)) return cls;
  }
  return null;
}

/**
 * The control to focus when moving one row from `active`, or null to stay put.
 *
 * Returns null rather than throwing or wrapping around: at the first or last
 * row the caret should simply not move, and silently jumping to the other end
 * of a 500-row matrix would lose the reviewer's place.
 */
export function nextCellTarget(
  active: Element | null | undefined,
  dir: 1 | -1,
): HTMLElement | null {
  if (!active) return null;

  const cell = active.closest("td");
  const col = columnClassOf(cell);
  const row = active.closest("tr");
  if (!cell || !col || !row) return null;

  // Walk siblings rather than taking the immediate one: a future spacer or
  // group row would otherwise stop navigation dead.
  let sibling: Element | null =
    dir === 1 ? row.nextElementSibling : row.previousElementSibling;
  while (sibling && sibling.tagName !== "TR") {
    sibling = dir === 1 ? sibling.nextElementSibling : sibling.previousElementSibling;
  }
  if (!sibling) return null;

  const selector = FOCUSABLE_TAGS.map((tag) => `td.${col} ${tag}`).join(", ");
  const target = sibling.querySelector(selector);
  return target instanceof HTMLElement ? target : null;
}

/**
 * Whether this event should move between rows.
 *
 * Deliberately narrow: Alt with an arrow, and no other modifier. Requiring the
 * others to be absent keeps combinations like Ctrl+Alt+Arrow — which some
 * desktops bind to workspace switching — from being swallowed here.
 */
export function isRowNavEvent(e: {
  key: string;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}): 1 | -1 | null {
  if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return null;
  if (e.key === "ArrowDown") return 1;
  if (e.key === "ArrowUp") return -1;
  return null;
}
